import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  User, MessageSquare, CalendarDays, Calendar, Ticket, History, Fuel,
  Users, ShoppingBag, Package, Globe, AlertTriangle, Shield, HelpCircle,
  Settings, SlidersHorizontal, Bell, Download, LogOut, Heart, Zap,
  ChevronRight, X, Car, PartyPopper, Hand, Sparkles
} from 'lucide-react';
import { GuestFade } from '@/components/AuthGateDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Native-app style "More" sheet.
 *
 * Replaces the previous flat 3-column sitemap-style grid with:
 *  - profile hero (avatar + name + dashboard CTA)
 *  - featured tiles (2x2 colourful cards for high-value flows)
 *  - grouped sections (iOS-Settings vibe)
 *
 * Render this inside a parent that controls visibility (animated drawer).
 * Keeps Navbar.js cleaner.
 */

const sectionWrap = "rounded-2xl bg-card/60 border border-border/40 overflow-hidden";

function Row({ to, onClick, icon: Icon, label, sub, tone = 'cyan', badge, danger, gold, last, testid }) {
  // tone -> background ring colour for the icon badge
  const tones = {
    cyan: 'bg-cyan-500/15 text-cyan-300',
    pink: 'bg-pink-500/15 text-pink-300',
    amber: 'bg-amber-500/15 text-amber-300',
    violet: 'bg-violet-500/15 text-violet-300',
    emerald: 'bg-emerald-500/15 text-emerald-300',
    blue: 'bg-blue-500/15 text-blue-300',
    rose: 'bg-rose-500/15 text-rose-300',
    slate: 'bg-slate-500/15 text-slate-300',
  };
  const iconCls = danger
    ? 'bg-red-500/15 text-red-400'
    : gold
      ? 'bg-yellow-500/15 text-yellow-400'
      : tones[tone] || tones.cyan;

  const content = (
    <div className={`flex items-center gap-3 px-4 py-3 ${last ? '' : 'border-b border-border/30'} ${danger ? 'text-red-400' : ''}`}>
      <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconCls}`}>
        <Icon className="w-4 h-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span className={`block text-sm font-semibold truncate ${danger ? 'text-red-400' : gold ? 'text-yellow-400' : 'text-foreground'}`}>{label}</span>
        {sub && <span className="block text-[11px] text-muted-foreground truncate">{sub}</span>}
      </span>
      {badge > 0 && (
        <span className="bg-pink-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center flex-shrink-0">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
      <ChevronRight className="w-4 h-4 text-muted-foreground/50 flex-shrink-0" />
    </div>
  );

  if (to) return <Link to={to} onClick={onClick} data-testid={testid} className="block hover:bg-foreground/5 active:bg-foreground/10 transition-colors">{content}</Link>;
  return <button type="button" onClick={onClick} data-testid={testid} className="w-full text-left hover:bg-foreground/5 active:bg-foreground/10 transition-colors">{content}</button>;
}

function SectionHeader({ children }) {
  return (
    <div className="px-1 pt-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
      {children}
    </div>
  );
}

function FeatureTile({ to, onClick, icon: Icon, label, sub, subClass, tone, badge, testid }) {
  // tone -> gradient
  const grads = {
    cyan: 'from-cyan-500/20 to-blue-500/10 border-cyan-400/30',
    pink: 'from-pink-500/20 to-rose-500/10 border-pink-400/30',
    violet: 'from-violet-500/20 to-fuchsia-500/10 border-violet-400/30',
    emerald: 'from-emerald-500/20 to-teal-500/10 border-emerald-400/30',
  };
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`relative group block p-4 rounded-2xl bg-gradient-to-br ${grads[tone] || grads.cyan} border-2 hover:scale-[1.02] active:scale-[0.99] transition-transform`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <Icon className={`w-7 h-7 ${tone === 'cyan' ? 'text-cyan-300' : tone === 'pink' ? 'text-pink-300' : tone === 'violet' ? 'text-violet-300' : 'text-emerald-300'}`} />
        {badge > 0 && (
          <span className="bg-pink-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </div>
      <p className="text-sm font-bold mt-2">{label}</p>
      <p className={`text-[11px] mt-0.5 leading-tight ${subClass || 'text-muted-foreground'}`}>{sub}</p>
    </Link>
  );
}

function QuickActionTile({ to, onClick, icon: Icon, label, tone, testid }) {
  // Compact action tile — vertical icon + label, glowing on hover
  const tones = {
    primary: 'from-primary/25 to-primary/5 border-primary/40 text-primary',
    cyan: 'from-cyan-500/25 to-cyan-500/5 border-cyan-400/40 text-cyan-300',
    amber: 'from-amber-500/25 to-amber-500/5 border-amber-400/40 text-amber-300',
    emerald: 'from-emerald-500/25 to-emerald-500/5 border-emerald-400/40 text-emerald-300',
  };
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1.5 py-3 px-2 rounded-xl bg-gradient-to-br ${tones[tone] || tones.primary} border hover:scale-[1.03] active:scale-[0.98] transition-transform`}
      data-testid={testid}
    >
      <Icon className="w-5 h-5" />
      <span className="text-[11px] font-bold text-foreground text-center leading-tight">{label}</span>
    </Link>
  );
}

export default function MoreMenuSheet({
  user, listingInquiries = {}, crisisZoneVisible,
  pushSupported, pushSubscribed, pushLoading, isStandalone,
  onPushSubscribe, onInstall, onLogout, onClose,
  handleTouchStart, handleTouchMove, handleTouchEnd,
  activeBookingsCount = 0,
}) {
  const navigate = useNavigate();
  const close = () => onClose?.();
  const isAdmin = !!(user && (user.is_admin || user.role === 'admin'));
  const initials = (user?.name || '?').split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  // Upcoming events — used to dot the mini-calendar 7-day strip so the
  // menu's calendar tile matches the dashboard widget's behaviour. The
  // /events endpoint is public, no auth required.
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/events?upcoming_only=true&limit=8`)
      .then(res => { if (!cancelled) setUpcomingEvents(res.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Full-viewport sheet: starts at y=0 and overlays the navbar (z-1000 vs
  // navbar z-50). This lets menu items scroll OVER the navbar instead of
  // being clipped under it when the user scrolls the menu.
  return (
    <div
      className="md:hidden bg-background fixed inset-x-0 top-0 bottom-0 z-[1000] overflow-y-auto animate-fade-in"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        overscrollBehavior: 'contain',
      }}
      data-testid="more-menu-sheet"
    >
      {/* Drag handle */}
      <div className="flex justify-center pt-2 pb-1 cursor-grab active:cursor-grabbing"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
      </div>

      <div className="px-4 pb-6 space-y-3">
        {/* Dashboard-style header — RollM8s logo icon sits next to the
            `Hey, {name}` greeting (same pattern as DashboardPage where the
            avatar sits next to the heading), with an X close button on the
            right where the navbar's hamburger would be. Logo is intentionally
            non-clickable. */}
        {user ? (
          <div className="grid grid-cols-[40px_1fr_40px] items-center gap-2 pt-1 pb-2" data-testid="more-greeting">
            <span aria-hidden className="w-10 h-10" />
            <div className="text-center min-w-0">
              <h2 className="font-heading text-3xl mb-1 leading-tight">
                Hey, <span className="gradient-text">{user.name?.split(' ')[0] || 'Skater'}</span>
              </h2>
              <p className="text-sm text-muted-foreground">What do you want to do today?</p>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close menu"
              data-testid="more-close-btn"
              className="w-10 h-10 rounded-full flex items-center justify-center text-foreground hover:bg-foreground/10 active:bg-foreground/20 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-[40px_1fr_40px] items-center gap-2 pt-1 pb-2">
            <span aria-hidden className="w-10 h-10" />
            <div className="text-center min-w-0">
              <h2 className="font-heading text-3xl mb-1 leading-tight">Welcome</h2>
              <p className="text-sm text-muted-foreground">Sign in to unlock everything</p>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close menu"
              data-testid="more-close-btn"
              className="w-10 h-10 rounded-full flex items-center justify-center text-foreground hover:bg-foreground/10 active:bg-foreground/20 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        )}

        {/* Profile hero — guests see "Guest User" and a tap lands them on
            /my-profile where theme + music settings are still usable. */}
        <Link to="/my-profile" onClick={close} data-testid="more-profile-hero"
          className="relative flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-br from-primary/15 to-secondary/10 border border-primary/30 hover:border-primary/60 transition-colors">
          <span className="absolute top-2 right-3 text-[9px] tracking-[0.18em] font-bold text-primary/80 uppercase">{user ? 'My Profile' : 'Profile'}</span>
          <div className="w-20 h-20 rounded-full bg-primary/20 ring-2 ring-primary/40 flex items-center justify-center overflow-hidden flex-shrink-0">
            {user && (user.selfie_url || user.picture) ? (
              <img src={user.selfie_url || user.picture} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-xl font-bold text-primary">{user ? initials : 'G'}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold truncate">{user ? (user.name || 'Skater') : 'Guest User'}</p>
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {user
                ? (isAdmin ? 'Admin' : (user.is_event_organiser ? 'Event organiser' : 'Tap to view profile'))
                : 'Tap to customise — sign up to unlock everything'}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground/60" />
        </Link>

        {/* Notifications — sits directly under the profile card so the user's
            identity is the first focal point, with their activity feed
            tucked right beneath it. */}
        <GuestFade locked={!user} feature="Notifications">
          <div className={sectionWrap}>
            <Row
              to="/notifications" onClick={close}
              icon={Bell} label="Notifications" sub="Activity feed & alerts"
              tone="amber" last
            />
          </div>
        </GuestFade>

        {/* Dashboard CTA — taller, more prominent */}
        <GuestFade locked={!user} feature="the Dashboard">
          <Link to="/dashboard" onClick={close} data-testid="more-dashboard-cta"
            className="block py-5 rounded-2xl text-base font-bold text-center text-primary border-2 border-primary bg-primary/5"
            style={{ boxShadow: '0 0 12px hsl(var(--primary) / 0.45), 0 0 24px hsl(var(--primary) / 0.2)' }}>
            Go to Dashboard
          </Link>
        </GuestFade>

        {/* Ticket Wallet CTA — uses the dual-wallet hero image so it
            visually anchors the menu as the most premium tile. Tapping
            opens the wallet view at /my-tickets. */}
        <GuestFade locked={!user} feature="the Ticket Wallet">
          <Link to="/my-tickets" onClick={close} data-testid="more-ticket-wallet-cta"
            className="block relative rounded-2xl overflow-hidden border-2 border-cyan-500/60 bg-black"
            style={{ boxShadow: '0 0 14px rgb(6 182 212 / 0.45), 0 0 28px rgb(236 72 153 / 0.25)' }}>
            <img
              src="/ticket-wallet-interior.jpg"
              alt="Open your Ticket Wallet"
              width="900"
              height="600"
              className="w-full h-auto block select-none"
              loading="lazy"
              decoding="async"
              draggable="false"
            />
            <div className="absolute inset-x-0 bottom-0 px-4 py-2.5 bg-gradient-to-t from-black via-black/85 to-transparent flex items-center justify-center">
              <span className="text-base font-bold tracking-wide bg-gradient-to-r from-fuchsia-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Open Ticket Wallet
              </span>
            </div>
          </Link>
        </GuestFade>

        {/* Primary action grid — most-tapped flows surfaced first.
            Row 1 (2-col big tiles):  Create Event · View Events
            Row 2 (2-col big tiles):  Community Listings · Hire Hub
            Row 3 (3-col compact):    World Trips · Offer a Ride · Request a Ride */}
        {/* Row 1 + Row 2: big featured tiles */}
        <div className="grid grid-cols-2 gap-2">
          <GuestFade locked={!user} feature="Upload Flyer / Create Event">
            <FeatureTile
              to="/create-event" onClick={close} testid="more-tile-create-event"
              icon={PartyPopper} label="Upload Flyer / Create Event" sub="List a new event"
              tone="emerald"
            />
          </GuestFade>
          <FeatureTile
            to="/events" onClick={close} testid="more-tile-events"
            icon={CalendarDays} label="View Events" sub="Discover what's on"
            tone="cyan"
          />
          <FeatureTile
            to="/sk8-shop" onClick={close} testid="more-tile-sk8shop"
            icon={ShoppingBag} label="Community Listings" sub="Buy · Sell · Swap"
            subClass="text-white font-semibold"
            tone="pink" badge={listingInquiries.sk8shop || 0}
          />
          <FeatureTile
            to="/hire" onClick={close} testid="more-tile-hire"
            icon={Package} label="Hire Hub" sub="Rent equipment"
            tone="violet" badge={listingInquiries.hire || 0}
          />
        </div>

        {/* Row 3: compact action tiles */}
        <div className="grid grid-cols-3 gap-2">
          <QuickActionTile
            to="/world-trips" onClick={close} testid="more-tile-trips"
            icon={Globe} label="World Trips" tone="emerald"
          />
          <GuestFade locked={!user} feature="Offer a Ride">
            <QuickActionTile
              to="/create-trip" onClick={close} testid="more-action-offer-ride"
              icon={Car} label="Offer a Ride" tone="cyan"
            />
          </GuestFade>
          <GuestFade locked={!user} feature="Request a Ride">
            <QuickActionTile
              to="/request-ride" onClick={close} testid="more-action-request-ride"
              icon={Hand} label="Request a Ride" tone="amber"
            />
          </GuestFade>
        </div>

        {/* My Bookings — sits directly under the World Trips / Offer / Request
            row so users find their active rides, hires & tickets in the same
            visual neighbourhood as the actions that create them. */}
        <GuestFade locked={!user} feature="My Bookings">
          <div className={sectionWrap}>
            <Row
              to="/my-bookings" onClick={close}
              icon={Ticket} label="My Bookings" sub="Rides · Hire · Tickets"
              tone="emerald" badge={activeBookingsCount} last
              testid="more-my-bookings"
            />
          </div>
        </GuestFade>

        {/* Notifications + My Bookings live at the top of the menu now
            (above + below the profile hero respectively). No duplicate
            entries here. */}

        {/* ACTIVITY */}
        <GuestFade locked={!user} feature="My Activity">
          <>
            <SectionHeader>My activity</SectionHeader>

            {/* Mini-calendar tile — mirrors the dashboard widget's visual
                identity (gradient + glow + today's date + 7-day strip)
                so the menu entry reads as a real calendar, not just a
                text row. Computed client-side from today's date — no
                data fetch needed. Placed ABOVE the activity list so the
                calendar is the first thing the user sees in this section. */}
            <Link
              to="/calendar"
              onClick={close}
              data-testid="more-tile-calendar"
              className="relative overflow-hidden rounded-2xl p-4 mb-3 block hover:scale-[1.01] active:scale-[0.99] transition-transform"
              style={{
                background: 'linear-gradient(135deg, #1e0b3b, #120B29)',
                border: '1px solid rgba(255,255,255,0.06)',
                boxShadow: '0 8px 24px rgba(217,70,239,0.12)',
              }}
            >
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-cyan-500/20 blur-3xl" />
              <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-fuchsia-500/20 blur-3xl" />
              {(() => {
                const today = new Date();
                const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                const weekEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 6, 23, 59, 59);
                const eventDatesThisWeek = new Set();
                const thisWeekEvents = (upcomingEvents || []).filter(ev => {
                  if (!ev.date) return false;
                  const evDate = new Date(ev.date);
                  return evDate >= startOfToday && evDate <= weekEnd;
                });
                thisWeekEvents.forEach(ev => {
                  const d = new Date(ev.date);
                  eventDatesThisWeek.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
                });
                const days = Array.from({ length: 7 }, (_, i) => {
                  const d = new Date(today);
                  d.setDate(today.getDate() + i);
                  const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                  return { day: dayNames[d.getDay()], date: d.getDate(), isToday: i === 0, hasEvent: eventDatesThisWeek.has(key) };
                });
                const nextEvent = thisWeekEvents.length > 0 ? thisWeekEvents[0] : null;
                return (
                  <>
                    <div className="relative z-10 flex items-center gap-3">
                      <div className="flex flex-col items-center min-w-[58px]">
                        <span className="text-[9px] text-cyan-400 uppercase tracking-[0.2em] font-bold">
                          {dayNames[today.getDay()]}
                        </span>
                        <span className="text-3xl font-black text-white leading-none drop-shadow-lg">
                          {today.getDate()}
                        </span>
                        <span className="text-[9px] text-zinc-500 uppercase tracking-widest">
                          {monthNames[today.getMonth()]}
                        </span>
                      </div>
                      <div className="w-px h-12 bg-gradient-to-b from-transparent via-cyan-400/30 to-transparent" />
                      <div className="flex-1 min-w-0 space-y-1">
                        <p className="text-sm font-bold text-white">Calendar</p>
                        <div className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_6px_rgba(34,211,238,0.8)]" />
                          <span className="text-[11px] text-zinc-300" data-testid="menu-calendar-event-count">
                            {thisWeekEvents.length} event{thisWeekEvents.length !== 1 ? 's' : ''} this week
                          </span>
                        </div>
                        {nextEvent ? (
                          <div className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500 shadow-[0_0_6px_rgba(217,70,239,0.8)]" />
                            <span className="text-[11px] text-zinc-400 truncate" data-testid="menu-calendar-next-event">
                              {nextEvent.name}
                            </span>
                          </div>
                        ) : (
                          <p className="text-[11px] text-zinc-500 italic truncate">No upcoming events</p>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between gap-1 mt-3 pt-2.5 border-t border-white/5 relative z-10">
                      {days.map((d, i) => (
                        <div key={i} className={`flex flex-col items-center flex-1 ${d.isToday ? 'text-cyan-300' : 'text-zinc-500'}`}>
                          <span className="text-[8px] uppercase tracking-wider opacity-70">{d.day}</span>
                          <span
                            data-testid={`menu-calendar-day-${i}${d.hasEvent ? '-has-event' : ''}`}
                            className={`text-[11px] font-bold mt-0.5 grid place-items-center w-5 h-5 rounded-full ${
                              d.isToday
                                ? 'bg-cyan-400/20 ring-1 ring-cyan-400/60 text-cyan-300'
                                : d.hasEvent
                                  ? 'bg-fuchsia-500/15 ring-1 ring-fuchsia-400/50 text-fuchsia-300'
                                  : ''
                            }`}
                          >
                            {d.date}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </Link>

            <div className={sectionWrap}>
              <Row to="/trip-history" onClick={close} icon={History} label="My Trips" sub="Driver & rider history" tone="violet" />
              <Row to="/messages" onClick={close} icon={MessageSquare} label="Messages" tone="blue" />
              <Row to="/civilian" onClick={close} icon={Sparkles} label="Civilian Nights" sub="Raves, clubs, day parties" tone="violet" last />
            </div>
          </>
        </GuestFade>

        {/* COMMUNITY */}
        <SectionHeader>Community</SectionHeader>
        <div className={sectionWrap}>
          <Row to="/rollflix" onClick={close} icon={Users} label="Community" sub="Find your people" tone="cyan" />
          <Row to="/safe-space" onClick={close} icon={Heart} label="Safe Space" sub="Support & wellbeing" tone="rose" />
          <Row to="/ranting-spot" onClick={close} icon={MessageSquare} label="Unfiltered" sub="Open community board" tone="violet" last />
        </div>

        {/* TOOLS */}
        <SectionHeader>Tools</SectionHeader>
        <div className={sectionWrap}>
          <Row to="/fuel-estimator" onClick={close} icon={Fuel} label="Fuel Estimator" sub="Calculate ride contributions" tone="amber" />
          <Row to="/floor-tax" onClick={close} icon={Zap} label="Floor Tax" sub="Skate tax pot" tone="emerald" last />
        </div>

        {/* SAFETY & SUPPORT */}
        <SectionHeader>Help & safety</SectionHeader>
        <div className={sectionWrap}>
          <Row to="/safety" onClick={close} icon={Shield} label="Safety" sub="Tips, tools, panic" tone="emerald" />
          {crisisZoneVisible && (
            <Row to="/blackout" onClick={close} icon={AlertTriangle} label="Blackout Zone" sub="Offline survival map" tone="amber" />
          )}
          <GuestFade locked={!user} feature="Support">
            <Row to="/support" onClick={close} icon={HelpCircle} label="Support" sub="Get help fast" tone="blue" />
          </GuestFade>
          <Row to="/faq" onClick={close} icon={HelpCircle} label="FAQ" sub="Common questions" tone="slate" last />
        </div>

        {/* ADMIN (admin only) */}
        {isAdmin && (
          <>
            <SectionHeader>Admin</SectionHeader>
            <div className={sectionWrap}>
              <Row to="/admin" onClick={close} icon={Settings} label="Admin Panel" gold />
              <Row to="/site-settings" onClick={close} icon={SlidersHorizontal} label="Site Settings" gold last />
            </div>
          </>
        )}

        {/* ACCOUNT FOOTER — only shown for signed-in users. Guests just
            see the Install button (if applicable) as a standalone tile.
            Showing a faded "Profile / Log out" section to guests was
            confusing — Profile is already in the hero above, and Log
            out is meaningless when there's no session. */}
        {user ? (
          <>
            <SectionHeader>Account</SectionHeader>
            <div className={sectionWrap}>
              <Row to="/my-profile" onClick={close} icon={User} label="Profile" tone="slate" />
              {pushSupported && !pushSubscribed && (
                <Row onClick={() => { onPushSubscribe?.(); }} icon={Bell} label={pushLoading ? 'Subscribing…' : 'Enable alerts'} sub="Push notifications" tone="cyan" />
              )}
              {!isStandalone && (
                <Row onClick={() => { onInstall?.(); close(); }} icon={Download} label="Install app" sub="Add to home screen" tone="violet" />
              )}
              <Row onClick={onLogout} icon={LogOut} label="Log out" danger last />
            </div>
          </>
        ) : (
          !isStandalone && (
            <div className={sectionWrap}>
              <Row onClick={() => { onInstall?.(); close(); }} icon={Download} label="Install app" sub="Add to home screen" tone="violet" last />
            </div>
          )
        )}

        {/* Close at bottom for thumb reach */}
        <button
          type="button"
          onClick={close}
          className="w-full py-3 mt-2 rounded-full bg-background/60 border border-border/50 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-foreground/5"
          data-testid="more-menu-close"
        >
          <X className="w-4 h-4" /> Close
        </button>
      </div>
    </div>
  );
}
